<template>
  <div class="d-flex mt-4">
    <div class="mr-3">
      <Timeline
        :snapshot="snapshot"
        :comboStringsBySubrange="comboStringsBySubrange"
        @activateSnapshot="snapshotToData"
        :savedHistory="timelineSave"
      />
    </div>

    <div class="mr-3">
      <RangeGrid
        :subrangeByComboString="subrangeByComboString"
        :boardFilteredComboStrings="boardFilteredComboStrings"
        :colorScheme="colorScheme"
        :boardSuits="boardSuits"
        :markedSquareStrings="markedSquareStrings"
        @toggleSquare="toggleSquare"
        @mouseoverSquare="mouseoverSquare"
        @mouseoutSquare="mouseoutSquare"
      />

      <b-textarea
        v-if="notes && takingScreenshot"
        v-model="notes"
        placeholder="Notes"
        class="mt-3"
      />

      <div class="mt-3" v-if="!takingScreenshot">
        <SuitSelect
          v-model="selectedSuitComboStrings"
          :subranges="subrangeDefinitions"
          :colorScheme="colorScheme"
          :board="board"
          :activeSubrangeIndex="activeSubrangeIndex"
          :alternateSubrangeIndex="alternateSubrangeIndex"
          :highlightSuits="hoveringSquareSuits"
          @changeAlternateSubrangeIndex="setAlternateSubrangeIndex"
        />
      </div>
    </div>

    <div class="rightColumn">
      <SubrangeList
        :subranges="subrangeDefinitions"
        :colorScheme="colorScheme"
        :activeSubrangeIndex="activeSubrangeIndex"
        :comboCounts="comboCounts"
        @activateSubrange="activateSubrange"
        class="mb-3"
        :colorSchemeOptions="colorSchemeOptions"
        :actionSetOptions="actionSetOptions"
        @changeActionSet="setActionSetId"
        @changeColorScheme="setColorSchemeId"
        @clearGrid="clearGrid"
      />

      <Board
        v-if="showBoard"
        v-model="board"
        :fixedCards="fixedCards"
        :isEditable="canEditBoard"
        class="mb-3"
      />

      <HandTypes
        v-if="board.length >= 3"
        :board="board"
        :comboStrings="comboStringsExcludingBoardFiltered"
        :resetOnChange="snapshotLoadedTime"
        @updateMarkedSquareStrings="updateMarkedSquareStrings"
        class="mb-3"
      />

      <b-textarea v-if="!takingScreenshot" v-model="notes" placeholder="Notes" />

      <b-button
        v-if="!takingScreenshot"
        class="mt-3"
        @click="screenshot"
        :disabled="takingScreenshot"
      >
        <div v-if="!takingScreenshot">Screenshot</div>
        <div v-else><fa icon="spinner-third" spin /></div>
      </b-button>
    </div>
  </div>
</template>

<script>
import mapValues from "lodash/mapValues";
import countBy from "lodash/countBy";
import difference from "lodash/difference";
import qs from "query-string";
import html2canvas from "html2canvas";
import canvasToImage from "canvas-to-image";
import RangeGrid from "./RangeGrid";
import {
  allComboStrings,
  allSuitComboStrings,
  comboStringsToSuitPartition,
  comboStringToSuitComboString,
  excludeCardsFromComboStrings,
  squareStringToComboStrings,
  stringToCards,
  subrangeByComboStringToComboStringsBySubrange,
  cardsToSuits,
  allCombos,
} from "../helpers/cards";
import SubrangeList from "./SubrangeList";
import Board from "./Board";
import SuitSelect from "./SuitSelect";
import { arrayStringEquals } from "../helpers/collections";
import { COLOR_SCHEMES, RANGEGROUP_TYPES } from "../constants";
import Timeline from "./Timeline";
import { timelineSave } from "../demoContent";
import debounce from "../mixins/debounce";
import HandTypes from "./HandTypes";

export default {
  name: "PageRangeAnalyzer",

  components: { HandTypes, Timeline, SuitSelect, Board, SubrangeList, RangeGrid },

  mixins: [debounce],

  data() {
    const queryString = qs.parse(window.location.search);
    const alternateByActiveSubrangeIndex = [-1, -1, -1, -1, -1];
    return {
      activeSubrangeIndex: 0,
      alternateByActiveSubrangeIndex,
      selectedSuitComboStrings: [],
      board: queryString.board ? stringToCards(queryString.board) : [],
      squareToggleActivatesSquares: true,
      hoveringSquare: null,
      notes: "",
      activeActionSetId: "preflop",
      activeColorSchemeId: "default",
      takingScreenshot: false,
      subrangeByComboString: {},
      history: [],
      // The number of cards on the board that can't be edited any more
      fixedCards: 0,
      canEditBoard: true,
      timelineSave: queryString.save === "pchw0320" ? timelineSave : null,
      debounced: { hoveringSquare: 50, subrangeByComboString: 100 },
      markedSquareStrings: [],
      // Keep track when the last history snapshot was loaded. This can be used to tell
      // child components that don't work well across snapshots that they might want to reset.
      snapshotLoadedTime: 0,
    };
  },

  created() {
    this.clearGrid();
  },

  computed: {
    trashRangeIndex() {
      return this.subrangeDefinitions.length - 1;
    },
    colorScheme() {
      // Just a hack to make the fold range white instead of dark preflop.
      // If this ever goes into production, color management should be rewritten from scratch.
      const colors = [...COLOR_SCHEMES[this.activeColorSchemeId].colors];
      if (this.actionSet.id.match(/preflop/)) {
        colors[colors.length - 1] = "#ffffff";
      }
      return colors;
    },
    actionSet() {
      return RANGEGROUP_TYPES[this.activeActionSetId];
    },
    subrangeDefinitions() {
      return this.actionSet.subranges;
    },
    alternateSubrangeIndex() {
      return this.alternateByActiveSubrangeIndex[this.activeSubrangeIndex];
    },
    activeSuitComboStrings() {
      // If no suits are selected, that means that all suits are active
      return this.selectedSuitComboStrings.length === 0
        ? allSuitComboStrings
        : this.selectedSuitComboStrings;
    },
    boardSuits() {
      return cardsToSuits(this.board);
    },
    showBoard() {
      return this.board.length || this.canEditBoard;
    },
    boardFilteredComboStrings() {
      return excludeCardsFromComboStrings(allComboStrings, this.board);
    },
    comboStringsBySubrange() {
      return subrangeByComboStringToComboStringsBySubrange(this.debounced.subrangeByComboString);
    },
    comboStringsBySubrangeExcludingBoard() {
      return mapValues(this.comboStringsBySubrange, comboStrings => {
        return excludeCardsFromComboStrings(comboStrings, this.board);
      });
    },
    comboCounts() {
      const hands = [].concat(...Object.values(this.comboStringsBySubrangeExcludingBoard));
      const handsInRange = [].concat(
        ...Object.values(this.comboStringsBySubrangeExcludingBoardFiltered)
      );
      return {
        total: hands.length,
        totalInRange: handsInRange.length,
        bySubrangeIndex: mapValues(
          this.comboStringsBySubrangeExcludingBoard,
          comboStrings => comboStrings.length
        ),
      };
    },
    hoveringSquareSuits() {
      if (!this.debounced.hoveringSquare) return null;
      const hoveringComboStrings = squareStringToComboStrings(this.debounced.hoveringSquare);
      const withoutBoard = excludeCardsFromComboStrings(hoveringComboStrings, this.board);
      const filteredComboStrings = withoutBoard.filter(
        comboString => this.subrangeByComboString[comboString] > -1
      );
      return filteredComboStrings.reduce((obj, comboString) => {
        obj[comboStringToSuitComboString(comboString)] = this.subrangeByComboString[comboString];
        return obj;
      }, {});
    },
    actionSetOptions() {
      return Object.values(RANGEGROUP_TYPES).map(data => {
        return {
          id: data.id,
          label: data.label,
        };
      });
    },
    colorSchemeOptions() {
      return Object.values(COLOR_SCHEMES).map(data => {
        return {
          id: data.id,
          label: data.label,
        };
      });
    },
    // Everything that needs to be persisted to history
    snapshot() {
      return {
        board: this.board,
        notes: this.notes,
        activeActionSetId: this.activeActionSetId,
        activeColorSchemeId: this.activeColorSchemeId,
        subrangeByComboString: this.subrangeByComboString,
        fixedCards: this.fixedCards,
      };
    },
    // Excluding combos that have been already been removed from the range
    comboStringsBySubrangeFiltered() {
      const result = { ...this.comboStringsBySubrange };
      delete result[-1];
      return result;
    },
    comboStringsBySubrangeExcludingBoardFiltered() {
      const result = { ...this.comboStringsBySubrangeExcludingBoard };
      delete result[-1];
      return result;
    },
    comboStringsExcludingBoardFiltered() {
      return [].concat(...Object.values(this.comboStringsBySubrangeExcludingBoardFiltered));
    },
    comboStringsNotInRange() {
      return this.comboStringsBySubrange[-1] || [];
    },
  },

  methods: {
    setActionSetId(id) {
      this.activeActionSetId = id;
    },
    setColorSchemeId(id) {
      this.activeColorSchemeId = id;
    },
    clearGrid() {
      this.subrangeByComboString = allComboStrings.reduce((obj, comboString) => {
        // Make sure that combos that have been removed from the range previously stay removed
        const isExcludedFromRange = Number(this.subrangeByComboString[comboString]) === -1;
        obj[comboString] = isExcludedFromRange ? -1 : this.trashRangeIndex;
        return obj;
      }, {});
    },
    activateSubrange(index) {
      this.activeSubrangeIndex = index;
      if (this.alternateSubrangeIndex === index) {
        this.setAlternateSubrangeIndex((index + 1) % this.subrangeDefinitions.length);
      }
    },
    toggleSquare(squareString, isFirstSquare) {
      const allSquareComboStrings = squareStringToComboStrings(squareString);
      const squareComboStrings = difference(allSquareComboStrings, this.comboStringsNotInRange);
      // Partition all of the square's combos into those that match the selected suits and those that don't
      const suitPartition = comboStringsToSuitPartition(
        squareComboStrings,
        this.activeSuitComboStrings
      );

      // If this is the start of a multi-toggle, check on the first square whether it's activating or deactivating.
      // It's only activating if the active suit selection exactly matches the square's suit split.
      if (isFirstSquare) {
        const currentlySelected = squareComboStrings.filter(
          comboString => this.subrangeByComboString[comboString] == this.activeSubrangeIndex
        );
        const wouldBeSelected = suitPartition.matching;
        this.squareToggleActivatesSquares = !arrayStringEquals(
          currentlySelected,
          wouldBeSelected,
          true
        );
      }

      // If toggle is activating, put the combos into the currently active range
      if (this.squareToggleActivatesSquares) {
        for (let comboString of suitPartition.matching) {
          this.subrangeByComboString[comboString] = this.activeSubrangeIndex;
        }
      }
      // Else put them in the non-trash range that has the most combos already.
      // If there isn't such a range, put them in the trash range.
      else {
        const subranges = squareComboStrings.map(
          comboString => this.subrangeByComboString[comboString]
        );
        const subrangeCounts = countBy(subranges);
        const entryWithMostCombos = Object.entries(subrangeCounts)
          .filter(([i, count]) => i != this.activeSubrangeIndex && i != this.trashRangeIndex)
          .sort((a, b) => b[1] - a[1])[0];
        const targetRange =
          entryWithMostCombos !== undefined ? entryWithMostCombos[0] : this.trashRangeIndex;
        for (let comboString of suitPartition.matching) {
          this.subrangeByComboString[comboString] = targetRange;
        }
      }

      // If there are combos that were active before but are not any more because
      // the toggle removed suits from the active range, put them in the alternate/trash range
      const nonMatchingInActiveRange = suitPartition.notMatching.filter(
        comboString => this.subrangeByComboString[comboString] === this.activeSubrangeIndex
      );
      if (nonMatchingInActiveRange.length) {
        const targetRange =
          this.alternateSubrangeIndex < 0 ? this.trashRangeIndex : this.alternateSubrangeIndex;
        for (let comboString of nonMatchingInActiveRange) {
          this.subrangeByComboString[comboString] = targetRange;
        }
      }

      // Only change non matching combos in other ranges to a different range if
      // "action for other suits" is set and this is not a deactivating toggle
      for (let comboString of suitPartition.notMatching) {
        if (this.alternateSubrangeIndex < 0 || !this.squareToggleActivatesSquares) break;
        this.subrangeByComboString[comboString] = this.alternateSubrangeIndex;
      }
    },
    mouseoverSquare(squareString) {
      this.hoveringSquare = squareString;
    },
    mouseoutSquare() {
      this.hoveringSquare = null;
    },
    setAlternateSubrangeIndex(index) {
      this.$set(this.alternateByActiveSubrangeIndex, this.activeSubrangeIndex, index);
    },
    screenshot() {
      this.takingScreenshot = true;
      const activeSubrangeIndex = this.activeSubrangeIndex;
      this.activeSubrangeIndex = null;
      this.$nextTick(() => {
        setTimeout(async () => {
          const canvas = await html2canvas(document.body);
          this.activeSubrangeIndex = activeSubrangeIndex;
          this.takingScreenshot = false;
          canvasToImage(canvas, {
            name: `range-analyzer-${Date.now()}`,
            type: "png",
            quality: 1,
          });
        }, 100);
      });
    },
    async snapshotToData(snapshot) {
      for (const [property, value] of Object.entries(snapshot)) {
        this[property] = value;
      }
      await this.$nextTick();
      // Make sure selected subrange still exists after loading snapshot
      if (this.actionSet.subranges[this.activeSubrangeIndex].action === null) {
        this.activeSubrangeIndex = 0;
      }
      this.snapshotLoadedTime = Date.now();
    },
    updateMarkedSquareStrings(markedSquareStrings) {
      this.markedSquareStrings = markedSquareStrings;
    },
  },
};
</script>

<style scoped lang="scss">
.rightColumn {
  width: 350px;
}
</style>
